import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import * as moment from 'moment';

type LegalDateInput = string | number | Date | null | undefined;

interface LegalDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

@Injectable({
  providedIn: 'root'
})
export class LegalDateTimeService {
  public readonly legalTimezone = environment.legalTimezone || 'Australia/Brisbane';
  public readonly legalUtcOffsetMinutes = environment.legalUtcOffsetMinutes ?? 600;

  private readonly storageFormats = [
    'YYYY-MM-DDTHH:mm:ss.SSSSSSS',
    'YYYY-MM-DDTHH:mm:ss.SSSSSS',
    'YYYY-MM-DDTHH:mm:ss.SSSSS',
    'YYYY-MM-DDTHH:mm:ss.SSSS',
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DD HH:mm:ss.SSSSSSS',
    'YYYY-MM-DD HH:mm:ss.SSSSSS',
    'YYYY-MM-DD HH:mm:ss.SSSSS',
    'YYYY-MM-DD HH:mm:ss.SSSS',
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD'
  ];

  private readonly displayFormats = [
    'DD/MM/YYYY h:mm a',
    'D/M/YYYY h:mm a',
    'DD/MM/YYYY hh:mm a',
    'D/M/YYYY hh:mm a',
    'DD/MM/YYYY',
    'D/M/YYYY',
    'M/D/YYYY h:mm a',
    'M/D/YYYY',
    'MM/DD/YYYY h:mm a',
    'MM/DD/YYYY'
  ];

  format(value: LegalDateInput, pattern: string): string {
    const legalMoment = this.toLegalMoment(value);
    return legalMoment ? legalMoment.format(pattern) : '';
  }

  formatAudit(value: LegalDateInput, pattern = 'DD/MM/YYYY h:mm a'): string {
    const auditMoment = this.toAuditMoment(value);
    return auditMoment ? auditMoment.format(pattern) : '';
  }

  toStartOfDayIso(value: unknown): string | null {
    return this.toBoundaryIso(value, 'start');
  }

  toEndOfDayIso(value: unknown): string | null {
    return this.toBoundaryIso(value, 'end');
  }

  toAuditStartOfDayIso(value: unknown): string | null {
    return this.toBoundaryIso(value, 'start');
  }

  toAuditEndOfDayIso(value: unknown): string | null {
    return this.toBoundaryIso(value, 'end');
  }

  compare(left: LegalDateInput, right: LegalDateInput): number | null {
    const leftMs = this.toInstantMs(left);
    const rightMs = this.toInstantMs(right);

    if (leftMs === null || rightMs === null) {
      return null;
    }

    if (leftMs === rightMs) {
      return 0;
    }

    return leftMs > rightMs ? 1 : -1;
  }

  matches(left: LegalDateInput, operator: string, right: LegalDateInput): boolean {
    const comparison = this.compare(left, right);

    if (comparison === null) {
      return false;
    }

    switch (operator) {
      case '>':
        return comparison > 0;
      case '>=':
        return comparison >= 0;
      case '<':
        return comparison < 0;
      case '<=':
        return comparison <= 0;
      case '==':
      case '===':
        return comparison === 0;
      case '!=':
      case '!==':
        return comparison !== 0;
      default:
        return false;
    }
  }

  compareAudit(left: LegalDateInput, right: LegalDateInput): number | null {
    const leftMs = this.toAuditInstantMs(left);
    const rightMs = this.toAuditInstantMs(right);

    if (leftMs === null || rightMs === null) {
      return null;
    }

    if (leftMs === rightMs) {
      return 0;
    }

    return leftMs > rightMs ? 1 : -1;
  }

  matchesAudit(left: LegalDateInput, operator: string, right: LegalDateInput): boolean {
    const comparison = this.compareAudit(left, right);

    if (comparison === null) {
      return false;
    }

    switch (operator) {
      case '>':
        return comparison > 0;
      case '>=':
        return comparison >= 0;
      case '<':
        return comparison < 0;
      case '<=':
        return comparison <= 0;
      case '==':
      case '===':
        return comparison === 0;
      case '!=':
      case '!==':
        return comparison !== 0;
      default:
        return false;
    }
  }

  toAuditDate(value: LegalDateInput): Date | null {
    const auditMs = this.toAuditInstantMs(value);
    return auditMs === null ? null : new Date(auditMs);
  }

  auditAgo(value: LegalDateInput, reference: LegalDateInput = new Date()): string {
    const auditMs = this.toAuditInstantMs(value);
    const referenceMs = this.toAuditInstantMs(reference);

    if (auditMs === null || referenceMs === null) {
      return typeof value === 'string' ? value : '';
    }

    const seconds = Math.floor(Math.abs(referenceMs - auditMs) / 1000);

    if (seconds < 29) {
      return 'Just now';
    }

    const intervals: { [key: string]: number } = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
      second: 1
    };

    for (const key of Object.keys(intervals)) {
      const counter = Math.floor(seconds / intervals[key]);

      if (counter > 0) {
        return counter === 1 ? `${counter} ${key} ago` : `${counter} ${key}s ago`;
      }
    }

    return 'Just now';
  }

  nowAuditIso(): string {
    return new Date().toISOString();
  }

  toPickerDate(value: LegalDateInput): Date | null {
    const parts = this.extractWallClockParts(value);

    if (!parts) {
      return null;
    }

    return new Date(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      parts.millisecond
    );
  }

  fromPickerDate(value: unknown): string | null {
    const pickerDate = this.coercePickerDate(value);

    if (!pickerDate) {
      return null;
    }

    const utcMillis = Date.UTC(
      pickerDate.getFullYear(),
      pickerDate.getMonth(),
      pickerDate.getDate(),
      pickerDate.getHours(),
      pickerDate.getMinutes(),
      pickerDate.getSeconds(),
      pickerDate.getMilliseconds()
    ) - (this.legalUtcOffsetMinutes * 60 * 1000);

    return new Date(utcMillis).toISOString();
  }

  coercePickerDate(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      const numericDate = new Date(value);
      return Number.isNaN(numericDate.getTime()) ? null : numericDate;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
      return this.toPickerDate(value);
    }

    return null;
  }

  private toBoundaryIso(value: unknown, boundary: 'start' | 'end'): string | null {
    const parts = this.extractUnknownWallClockParts(value);

    if (!parts) {
      return null;
    }

    return new Date(this.wallClockPartsToInstantMs({
      ...parts,
      hour: boundary === 'start' ? 0 : 23,
      minute: boundary === 'start' ? 0 : 59,
      second: boundary === 'start' ? 0 : 59,
      millisecond: boundary === 'start' ? 0 : 999
    })).toISOString();
  }

  private extractUnknownWallClockParts(value: unknown): LegalDateParts | null {
    const pickerDate = this.coercePickerDate(value);

    if (pickerDate) {
      return {
        year: pickerDate.getFullYear(),
        month: pickerDate.getMonth() + 1,
        day: pickerDate.getDate(),
        hour: pickerDate.getHours(),
        minute: pickerDate.getMinutes(),
        second: pickerDate.getSeconds(),
        millisecond: pickerDate.getMilliseconds()
      };
    }

    if (typeof value === 'string') {
      return this.extractWallClockParts(value);
    }

    return null;
  }

  private toLegalMoment(value: LegalDateInput): moment.Moment | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }

      return moment.utc(value.getTime()).utcOffset(this.legalUtcOffsetMinutes);
    }

    if (typeof value === 'number') {
      return Number.isFinite(value)
        ? moment.utc(value).utcOffset(this.legalUtcOffsetMinutes)
        : null;
    }

    const zonedMoment = this.parseZonedMoment(value);

    if (zonedMoment) {
      return moment.utc(zonedMoment.valueOf()).utcOffset(this.legalUtcOffsetMinutes);
    }

    const naiveMoment = this.parseNaiveMoment(value);

    if (!naiveMoment) {
      return null;
    }

    return moment.utc([
      naiveMoment.year(),
      naiveMoment.month(),
      naiveMoment.date(),
      naiveMoment.hour(),
      naiveMoment.minute(),
      naiveMoment.second(),
      naiveMoment.millisecond()
    ]).utcOffset(this.legalUtcOffsetMinutes, true);
  }

  private toAuditMoment(value: LegalDateInput): moment.Moment | null {
    const auditMs = this.toAuditInstantMs(value);

    if (auditMs === null) {
      return null;
    }

    return moment.utc(auditMs).utcOffset(this.legalUtcOffsetMinutes);
  }

  private toInstantMs(value: LegalDateInput): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.getTime();
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const zonedMoment = this.parseZonedMoment(value);

    if (zonedMoment) {
      return zonedMoment.valueOf();
    }

    const parts = this.extractWallClockParts(value);

    if (!parts) {
      return null;
    }

    return this.wallClockPartsToInstantMs(parts);
  }

  private toAuditInstantMs(value: LegalDateInput): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.getTime();
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? this.normalizeUnixTimestamp(value) : null;
    }

    const numericString = this.parseNumericString(value);

    if (numericString !== null) {
      return numericString;
    }

    const zonedMoment = this.parseZonedMoment(value);

    if (zonedMoment) {
      return zonedMoment.valueOf();
    }

    const naiveMoment = this.parseNaiveMoment(value);

    if (!naiveMoment) {
      return null;
    }

    return this.wallClockPartsToInstantMs({
      year: naiveMoment.year(),
      month: naiveMoment.month() + 1,
      day: naiveMoment.date(),
      hour: naiveMoment.hour(),
      minute: naiveMoment.minute(),
      second: naiveMoment.second(),
      millisecond: naiveMoment.millisecond()
    });
  }

  private extractWallClockParts(value: LegalDateInput): LegalDateParts | null {
    const legalMoment = this.toLegalMoment(value);

    if (!legalMoment) {
      return null;
    }

    return {
      year: legalMoment.year(),
      month: legalMoment.month() + 1,
      day: legalMoment.date(),
      hour: legalMoment.hour(),
      minute: legalMoment.minute(),
      second: legalMoment.second(),
      millisecond: legalMoment.millisecond()
    };
  }

  private parseZonedMoment(value: string): moment.Moment | null {
    if (!this.hasZoneInformation(value)) {
      return null;
    }

    const zonedMoment = moment.parseZone(value, moment.ISO_8601, true);
    return zonedMoment.isValid() ? zonedMoment : null;
  }

  private parseNaiveMoment(value: string): moment.Moment | null {
    const formats = [...this.storageFormats, ...this.displayFormats];
    const naiveMoment = moment.utc(value, formats, true);
    return naiveMoment.isValid() ? naiveMoment : null;
  }

  private parseNumericString(value: string): number | null {
    const trimmedValue = value.trim();

    if (!/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
      return null;
    }

    const numericValue = Number(trimmedValue);
    return Number.isFinite(numericValue) ? this.normalizeUnixTimestamp(numericValue) : null;
  }

  private normalizeUnixTimestamp(value: number): number {
    return Math.abs(value) < 100000000000 ? Math.round(value * 1000) : Math.round(value);
  }

  private wallClockPartsToInstantMs(parts: LegalDateParts): number {
    return Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      parts.millisecond
    ) - (this.legalUtcOffsetMinutes * 60 * 1000);
  }

  private hasZoneInformation(value: string): boolean {
    return /(?:z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
  }
}
